import type { ModelId } from "@/hooks/use-model";
import type { DbAgent } from "@/types/db";

export type ModelProvider = "anthropic" | "openai" | "gemini";

export type ToolId =
  | "webSearch"
  | "webFetch"
  | "memory"
  | "codeExecution"
  | "knowledgeBase"
  | "myFiles"
  | "searchProjectMeetingMinutes"
  | "searchProjectChats"
  | "projectRegisters"
  | "resolveDateTime"
  | "writingStyleProfile"
  | "woodsIdentity";

/**
 * A single active tool to pass to the chat route.
 * Sourced from the `tools` table executor_config + per-instance config.
 */
export type ActiveToolConfig = {
  /** ToolId for builtins, or DB UUID for custom tools */
  toolId: string;
  executorConfig: {
    /** "anthropic" | "openai" | "internal" | "webhook" | "edge_function"
     *  Present on single-provider configs. Omit when using `providers`. */
    provider?: string;
    /** SDK method name / identifier. Present on single-provider configs. */
    feature_key?: string;
    /** Key used in the tools{} object sent to the model API */
    tool_name?: string;
    /** ToolId string for reverse lookup */
    tool_id?: string;
    /** Per-tool options, e.g. { max_uses: 10 } */
    options?: Record<string, unknown>;
    /**
     * Multi-provider shape — keyed by provider name ("anthropic", "openai", …).
     * When present, the route selects the sub-config that matches the active
     * model's provider. tool_id stays top-level; tool_name may be overridden
     * per provider — the sub-config is spread over the base, so a tool_name
     * here wins over the top-level one.
     *
     * Providers that expose the same capability MUST use distinct tool_names
     * (e.g. `web_search` for Anthropic, `web_search_openai` for OpenAI). The
     * tool_name is what gets persisted on every UI message part, and each
     * provider's prompt converter resolves history parts by NAME — a shared
     * name makes one provider validate the other's stored output against its
     * own schema and throw. See 20260824000001_namespace_openai_provider_tool_names.
     */
    providers?: Record<string, {
      feature_key: string;
      tool_type?: string;
      tool_name?: string;
      options?: Record<string, unknown>;
      [key: string]: unknown;
    }>;
    [key: string]: unknown;
  };
  /** Per-instance config, e.g. { fileIds, collectionIds } for knowledge_base */
  config?: Record<string, unknown>;
  /**
   * Present only for custom tools (source_type='custom' in the `tools` table).
   * "webhook" | "edge_function" | "aws_ecs" | "mcp" — mirrors the DB row's
   * executor_type column. Builtins omit this (dispatch is by provider/feature_key).
   */
  executorType?: string;
  /** Custom tools only — DB row `name`, used as the tool description fallback. */
  name?: string;
  /** Custom tools only — DB row `description`, sent to the model verbatim. */
  description?: string;
  /** Custom tools only — DB row `schema.parameters`, used to build the input schema. */
  schema?: {
    parameters?: Array<{
      name: string;
      type: "string" | "number" | "boolean" | "object" | "array";
      description?: string;
      required?: boolean;
    }>;
  } | null;
};

export type WebSearchToolConfig = {
  allowed_domains?: string[];
  blocked_domains?: string[];
};

export type KnowledgeBaseConfig = {
  fileIds?: string[];
  collectionIds?: string[];
};

export type ResolveDateTimeToolConfig = {
  defaultTimezone: string;
};

export const TOOL_IDS: ToolId[] = [
  "webSearch",
  "webFetch",
  "memory",
  "codeExecution",
  "knowledgeBase",
  "myFiles",
  "searchProjectMeetingMinutes",
  "searchProjectChats",
  "projectRegisters",
  "resolveDateTime",
  "writingStyleProfile",
  "woodsIdentity",
];

export interface UserSettings {
  model: ModelId;
  tools: Record<ToolId, boolean>;
  preferences: string;
  resolveDateTimeConfig: ResolveDateTimeToolConfig;
}

export interface AgentSettings {
  systemPrompt?: string;
  /** Builtin ToolIds and/or custom tool DB UUIDs assigned to this agent. */
  tools?: string[] | null;
  toolOverrides?: Partial<Record<ToolId, boolean>>;
}

export type ResolvedToolSetting = {
  userValue: boolean;
  agentValue: boolean | null;
  effectiveValue: boolean;
  source: "user" | "agent";
  overridden: boolean;
};

export const isToolId = (value: unknown): value is ToolId =>
  typeof value === "string" && TOOL_IDS.includes(value as ToolId);

export function resolveToolSettings(
  user: UserSettings,
  agent?: AgentSettings
): Record<ToolId, ResolvedToolSetting> {
  return TOOL_IDS.reduce<Record<ToolId, ResolvedToolSetting>>((resolved, tool) => {
    const userValue = user.tools[tool];
    const agentValue =
      agent?.tools != null
        ? agent.tools.includes(tool)
        : agent?.toolOverrides?.[tool] ?? null;
    const effectiveValue = agentValue ?? userValue;

    resolved[tool] = {
      userValue,
      agentValue,
      effectiveValue,
      source: agentValue === null ? "user" : "agent",
      overridden: agentValue !== null && agentValue !== userValue,
    };

    return resolved;
  }, {} as Record<ToolId, ResolvedToolSetting>);
}

/** Derive an agent's AgentSettings (builtin ToolIds only) from its DB record. */
export const getAgentSettings = (agent?: DbAgent | null): AgentSettings | undefined => {
  if (!agent) return undefined;

  if (Array.isArray(agent.tools)) {
    return {
      tools: agent.tools.filter(
        (tool): tool is NonNullable<AgentSettings["tools"]>[number] =>
          tool === "webSearch" ||
          tool === "webFetch" ||
          tool === "memory" ||
          tool === "codeExecution" ||
          tool === "knowledgeBase" ||
          tool === "myFiles" ||
          tool === "resolveDateTime" ||
          tool === "writingStyleProfile" ||
          tool === "woodsIdentity"
      ),
    };
  }

  return undefined;
};

/** Resolve effective tool flags. Agent settings override user settings. */
export function resolveTools(
  user: UserSettings,
  agent?: AgentSettings
): Record<ToolId, boolean> {
  const resolved = resolveToolSettings(user, agent);

  return TOOL_IDS.reduce<Record<ToolId, boolean>>((effective, tool) => {
    effective[tool] = resolved[tool].effectiveValue;
    return effective;
  }, {} as Record<ToolId, boolean>);
}
